import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { X, Upload, Paperclip, Trash2, Sparkles } from 'lucide-react';
import { expensesAPI } from '../../services/api';
import { EXPENSE_GROUPS, PAYMENT_METHODS } from '../../services/expenseCategories';
import LoadingSpinner from '../Common/LoadingSpinner';

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_BYTES = 10 * 1024 * 1024;

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  expense_date: todayISO(),
  category: '',
  description: '',
  amount: '',
  payment_method: 'ueberweisung',
  receipt_number: '',
  tax_deductible: true,
  notes: '',
};

const ExpenseForm = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => initial
    ? {
        expense_date: initial.expense_date,
        category: initial.category,
        description: initial.description,
        amount: String(initial.amount),
        payment_method: initial.payment_method,
        receipt_number: initial.receipt_number || '',
        tax_deductible: !!initial.tax_deductible,
        notes: initial.notes || '',
      }
    : emptyForm,
  );
  const [receiptFile, setReceiptFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [aiFilled, setAiFilled] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateFile = (f) => {
    if (!f) return false;
    if (!ALLOWED_MIME.includes(f.type)) {
      toast.error('Nur JPG, PNG, WebP oder PDF erlaubt.');
      return false;
    }
    if (f.size > MAX_BYTES) {
      toast.error('Datei zu groß (max. 10 MB).');
      return false;
    }
    return true;
  };

  const runExtraction = async (f) => {
    if (!f) return;
    setExtracting(true);
    const tid = toast.loading('Beleg wird mit KI gelesen…');
    try {
      const { data } = await expensesAPI.extract(f);
      const s = data?.suggestions || {};
      setForm((prev) => ({
        ...prev,
        expense_date: s.expense_date || prev.expense_date,
        category: s.category_key || prev.category,
        description: s.description || prev.description,
        amount: s.amount_gross != null ? String(s.amount_gross) : prev.amount,
        payment_method: s.payment_method || prev.payment_method,
        receipt_number: s.receipt_number || prev.receipt_number,
        notes: s.vendor && !prev.notes
          ? `Lieferant: ${s.vendor}${s.notes ? `\n${s.notes}` : ''}`
          : (s.notes || prev.notes),
      }));
      setAiFilled(true);
      toast.success('Vorschlag eingefügt – bitte prüfen.', { id: tid });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'KI-Auswertung fehlgeschlagen';
      toast.error(msg, { id: tid });
    } finally {
      setExtracting(false);
    }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!validateFile(f)) return;
    setReceiptFile(f);
    if (!isEdit) runExtraction(f);
  };

  const handleRerunExtraction = () => {
    if (receiptFile) runExtraction(receiptFile);
  };

  const uploadReceiptFor = async (expenseId) => {
    if (!receiptFile) return;
    setUploading(true);
    try {
      await expensesAPI.uploadReceipt(expenseId, receiptFile);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Beleg-Upload fehlgeschlagen');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const deleteExistingReceipt = async () => {
    if (!initial || !initial.receipt_path) return;
    if (!window.confirm('Beleg wirklich entfernen?')) return;
    try {
      await expensesAPI.deleteReceipt(initial.id);
      toast.success('Beleg entfernt');
      onSaved();
    } catch {
      toast.error('Entfernen fehlgeschlagen');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.category) { toast.error('Kategorie wählen'); return; }
    if (!form.description.trim()) { toast.error('Beschreibung fehlt'); return; }
    const amount = parseFloat(String(form.amount).replace(',', '.'));
    if (!amount || amount <= 0) { toast.error('Betrag muss > 0 sein'); return; }

    const payload = {
      expense_date: form.expense_date,
      category: form.category,
      description: form.description.trim(),
      amount,
      payment_method: form.payment_method,
      receipt_number: form.receipt_number.trim() || null,
      tax_deductible: form.tax_deductible,
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const { data } = await expensesAPI.update(initial.id, payload);
        saved = data;
      } else {
        const { data } = await expensesAPI.create(payload);
        saved = data;
      }
      if (receiptFile) {
        await uploadReceiptFor(saved.id);
      }
      toast.success(isEdit ? 'Gespeichert' : 'Ausgabe erfasst');
      onSaved();
    } catch (err) {
      if (!err.__uploadHandled) {
        toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
      }
    } finally {
      setSaving(false);
    }
  };

  const existingReceipt = initial && initial.receipt_path
    ? (initial.receipt_original_name || 'Beleg')
    : null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640, width: '92%' }}
      >
        <div className="modal-header">
          <h2>{isEdit ? 'Ausgabe bearbeiten' : 'Neue Ausgabe'}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Schließen">
            <X size={20} />
          </button>
        </div>

        {aiFilled && (
          <div
            style={{
              margin: '0.75rem 1rem 0',
              padding: '0.6rem 0.9rem',
              borderRadius: 8,
              background: 'rgba(99, 102, 241, 0.08)',
              border: '1px solid rgba(99, 102, 241, 0.25)',
              color: '#4338ca',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.88rem',
            }}
          >
            <Sparkles size={16} />
            <span>KI-Vorschlag eingefügt — bitte prüfen, anpassen und speichern.</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="expense-form-grid">
            <div className="form-group">
              <label className="form-label">Datum *</label>
              <input
                type="date"
                className="form-input"
                value={form.expense_date}
                onChange={(e) => set('expense_date', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Kategorie *</label>
              <select
                className="form-input"
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                required
              >
                <option value="">— wählen —</option>
                {EXPENSE_GROUPS.map((g) => (
                  <optgroup key={g.key} label={g.label}>
                    {g.categories.map((c) => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="form-group full">
              <label className="form-label">Beschreibung *</label>
              <input
                type="text"
                className="form-input"
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="z.B. Anthropic API April"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Betrag (€) *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                className="form-input"
                value={form.amount}
                onChange={(e) => set('amount', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Zahlungsart *</label>
              <select
                className="form-input"
                value={form.payment_method}
                onChange={(e) => set('payment_method', e.target.value)}
                required
              >
                {PAYMENT_METHODS.map((pm) => (
                  <option key={pm.key} value={pm.key}>{pm.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Belegnummer</label>
              <input
                type="text"
                className="form-input"
                value={form.receipt_number}
                onChange={(e) => set('receipt_number', e.target.value)}
                placeholder="Rechnung/Quittung Nr."
              />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginTop: '1.6rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.tax_deductible}
                  onChange={(e) => set('tax_deductible', e.target.checked)}
                />
                <span>Steuerlich absetzbar</span>
              </label>
            </div>

            <div className="form-group full">
              <label className="form-label">Notizen</label>
              <textarea
                className="form-input"
                rows={2}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                placeholder="z.B. Vertragsnummer, Zuordnung Projekt…"
              />
            </div>

            <div className="form-group full">
              <label className="form-label">Beleg (JPG, PNG, WebP, PDF · max 10 MB)</label>
              {existingReceipt && !receiptFile && (
                <div className="receipt-preview" style={{ marginBottom: 8 }}>
                  <span className="fname">
                    <Paperclip size={14} /> {existingReceipt}
                  </span>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={deleteExistingReceipt}
                    title="Beleg entfernen"
                  >
                    <Trash2 size={14} /> Entfernen
                  </button>
                </div>
              )}
              {receiptFile ? (
                <div className="receipt-preview">
                  <span className="fname">
                    <Paperclip size={14} /> {receiptFile.name}
                    <span className="text-gray-500" style={{ fontSize: '0.78rem', marginLeft: '.5rem' }}>
                      {(receiptFile.size / 1024).toFixed(0)} KB
                    </span>
                  </span>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    {!isEdit && (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={handleRerunExtraction}
                        disabled={extracting}
                        title="Felder erneut mit KI füllen"
                      >
                        <Sparkles size={14} /> {extracting ? 'Liest…' : 'KI erneut'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => { setReceiptFile(null); setAiFilled(false); }}
                      disabled={extracting}
                    >
                      Entfernen
                    </button>
                  </div>
                </div>
              ) : (
                <label className="receipt-drop">
                  <Upload size={18} style={{ marginBottom: 4 }} />
                  <div>
                    Beleg hochladen{existingReceipt ? ' (ersetzt bestehenden)' : ''}
                    {!isEdit && (
                      <div style={{ fontSize: '0.78rem', opacity: 0.7, marginTop: 4 }}>
                        ✨ wird automatisch mit KI ausgewertet
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={handleFile}
                    style={{ display: 'none' }}
                    disabled={extracting}
                  />
                </label>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-outline" disabled={saving || uploading || extracting}>
              Abbrechen
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || uploading || extracting}>
              {saving || uploading || extracting
                ? <LoadingSpinner size="small" />
                : (isEdit ? 'Speichern' : 'Erfassen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseForm;
